import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Bot,
  CreditCard,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Users,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { ROUTES } from '../../config/routes';
import { TrialCtaBanner } from '../../features/dashboard/components/TrialCtaBanner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { getTrialRemainingDays } from '../../lib/utils';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  children?: NavItem[];
  testId?: string;
  tooltipTestId?: string;
}

const clientNavItems: NavItem[] = [
  { path: ROUTES.HOME, label: 'ホーム', icon: <Home size={20} />, testId: 'sidebar-link-home', tooltipTestId: 'sidebar-tooltip-home' },
  { path: ROUTES.DOMAINS, label: 'チャットボット', icon: <Bot size={20} />, testId: 'sidebar-link-chatbots', tooltipTestId: 'sidebar-tooltip-chatbots' },
  { path: ROUTES.LEADS, label: 'リード', icon: <Users size={20} />, testId: 'sidebar-link-leads', tooltipTestId: 'sidebar-tooltip-leads' },
  { path: ROUTES.SUBSCRIPTION, label: 'プラン', icon: <CreditCard size={20} />, testId: 'sidebar-link-subscription', tooltipTestId: 'sidebar-tooltip-subscription' },
  { path: ROUTES.SETTINGS, label: '設定', icon: <Settings size={20} />, testId: 'sidebar-link-settings', tooltipTestId: 'sidebar-tooltip-settings' },
  { path: ROUTES.FAQ, label: 'ヘルプ', icon: <HelpCircle size={20} />, testId: 'sidebar-link-faq', tooltipTestId: 'sidebar-tooltip-faq' },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { sidebarCollapsed, toggleSidebarCollapse } = useUIStore();

  const trialRemainingDays = getTrialRemainingDays(user?.trial_ends_at);

  const isExpired = user?.plan === 'trial' && !!user?.trial_ends_at
    ? new Date(user.trial_ends_at) <= new Date()
    : false;

  const isActive = (path: string) => {
    if (path === ROUTES.HOME) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const NavItemComponent: React.FC<{ item: NavItem; nested?: boolean }> = ({ item, nested = false }) => {
    const active = isActive(item.path);

    return (
      <li className="list-none">
        <button
          onClick={() => handleNavClick(item.path)}
          data-testid={item.testId}
          className={[
            'w-full flex items-center min-h-[44px] md:min-h-[48px]',
            'border-l-[3px] transition-colors duration-150',
            sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-5',
            nested && !sidebarCollapsed ? 'pl-10' : '',
            active
              ? 'bg-[#F0FDFA] border-primary'
              : 'border-l-transparent hover:bg-primary/5',
          ].join(' ')}
        >
          {sidebarCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={['flex items-center justify-center flex-shrink-0', active ? 'text-primary' : 'text-text-secondary'].join(' ')}
                  data-testid={item.tooltipTestId}
                >
                  {item.icon}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={['flex items-center justify-center flex-shrink-0 mr-3', active ? 'text-primary' : 'text-text-secondary'].join(' ')}>
              {item.icon}
            </span>
          )}
          {!sidebarCollapsed && (
            <span className={[
              'text-sm truncate',
              active ? 'font-semibold text-primary' : 'font-normal text-text-primary',
            ].join(' ')}>
              {item.label}
            </span>
          )}
        </button>
      </li>
    );
  };

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, onMobileClose]);

  useEffect(() => {
    if (isExpired && !sidebarCollapsed) {
      api.post('/subscription/conversion-event', { event_type: 'banner_shown', source: 'expired_banner' }).catch(() => {});
    }
  }, [isExpired, sidebarCollapsed]);

  return (
    <>
      {/* モバイルオーバーレイ */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        data-testid="sidebar-nav"
        className={[
          'fixed inset-y-0 left-0 z-50 bg-white border-r border-border flex flex-col',
          'md:sticky md:top-0 md:h-screen md:overflow-y-auto md:z-auto md:flex-shrink-0',
          'transition-transform duration-200 ease-in-out',
          mobileOpen ? 'translate-x-0 visible' : '-translate-x-full invisible md:translate-x-0 md:visible',
          sidebarCollapsed ? 'w-[72px]' : 'w-60',
        ].join(' ')}
      >
        {/* ロゴ */}
        <div className={[
          'flex items-center flex-shrink-0 pt-5 mb-8',
          sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-5',
        ].join(' ')}>
          {!sidebarCollapsed ? (
            <Link to={ROUTES.HOME} style={{ textDecoration: 'none' }} className="flex items-center gap-2">
              <div
                className="w-9 h-9 flex items-center justify-center rounded-[10px] text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #0a7a72, #14B8A6)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
                </svg>
              </div>
              <span style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A' }}>EntryBOT</span>
            </Link>
          ) : null}
          <button
            onClick={toggleSidebarCollapse}
            data-testid="sidebar-toggle-desktop"
            className="hidden md:flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 text-text-secondary"
          >
            {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* ナビゲーション */}
        <nav className="flex-1 overflow-auto min-h-0 py-2">
          <TooltipProvider>
            <ul className="m-0 p-0">
              {clientNavItems.map((item) => (
                <NavItemComponent key={item.path} item={item} />
              ))}
            </ul>
          </TooltipProvider>
        </nav>

        {/* トライアル残日数バー */}
        {user?.plan === 'trial' && !isExpired && trialRemainingDays > 0 && (
          <div
            data-testid="trial-remaining-bar"
            className="px-3 py-2 mx-2 mb-2 rounded-lg bg-accent/10 border border-accent/20"
          >
            {!sidebarCollapsed && (
              <>
                <p className="text-xs font-medium text-accent-dark m-0">
                  {`トライアル残り ${trialRemainingDays}日`}
                </p>
                <div className="mt-1 h-1.5 rounded-full bg-accent/20">
                  <div
                    className="h-full rounded-full bg-accent"
                    style={{ width: `${Math.min(100, (trialRemainingDays / 14) * 100)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* 期限切れ常設バナー */}
        {isExpired && !sidebarCollapsed && (
          <div className="px-3 mb-2">
            <div
              data-testid="trial-expired-persistent-banner"
              className="rounded-lg p-3 border bg-red-50 border-red-300"
            >
              <p className="text-xs font-medium mb-2 text-red-700 m-0">
                期限切れ
              </p>
              <Button
                size="sm"
                data-testid="trial-expired-reactivate-link"
                className="w-full text-xs"
                onClick={() => {
                  api.post('/subscription/conversion-event', { event_type: 'cta_click', source: 'expired_banner' }).catch(() => {});
                  navigate(ROUTES.SUBSCRIPTION);
                }}
              >
                プランに加入して再開
              </Button>
            </div>
          </div>
        )}

        {/* トライアルCTAバナー */}
        {user?.plan === 'trial' && !sidebarCollapsed && !isExpired && (
          <div className="px-3 mb-2">
            <TrialCtaBanner />
          </div>
        )}

        {/* ユーザー情報 */}
        {!sidebarCollapsed && user && (
          <div className="p-4 border-t border-border bg-white flex-shrink-0 flex items-center gap-3" data-testid="sidebar-profile">
            <div
              className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
              style={{ background: '#E2E8F0' }}
            >
              <span style={{ fontSize: '13px', color: '#64748B', fontWeight: 500 }}>
                {(user.name || user.email || '?').charAt(0)}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-text-primary truncate m-0">
                {user.name || user.email}
              </p>
              <p className="text-xs text-text-secondary m-0">
                {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)} プラン
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};
